import React, { useMemo } from 'react';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  Popover,
  Wizard,
  WizardStep,
  ValidatedOptions,
  Alert,
} from '@patternfly/react-core';
import { ChooseBusinessUnitsStep } from './ChooseBU';
import { ClusterPicker } from './ClusterPicker';
import DependencySelector from './DependencySelector';
import ReviewStep from './ReviewStep';
import { Step1Footer, Step3Footer, StepResultsFooter } from './WizardFooters';
import { ExclamationTriangleIcon } from '@patternfly/react-icons';
import { CLUSTERS } from '@app/config';
import { useBusinessData } from '../hooks/useBusinessData';
import { useFormState } from '../hooks/useFormState';
import { useDeployment } from '../hooks/useDeployment';
import { isProdLikeCluster, isDevCluster } from '../helpers/clusterConfig';

globalThis.cluster_selected_value = localStorage.getItem("cluster_selected")
if (globalThis.cluster_selected_value == null || !Object.keys(CLUSTERS).includes(globalThis.cluster_selected_value)) {
  globalThis.cluster_selected_value = Object.values(CLUSTERS)[0].CLUSTER_NAME
  localStorage.setItem("cluster_selected", globalThis.cluster_selected_value);
  window.location.reload();
}

// This populates globalThis.cluster with the selected cluster information
globalThis.cluster = CLUSTERS[globalThis.cluster_selected_value]

const WIZARD_ROUTE = '/granular-deploy';

const CreateEnvWizard: React.FC = () => {
  const history = useHistory();
  const match = useRouteMatch(WIZARD_ROUTE);
  const { search } = useLocation();
  const isRefresh = new URLSearchParams(search).get('refresh') === 'true';
  const isDevClusterFlag = isDevCluster();
  
  const formState = useFormState(isRefresh, null, false); // businessData will be set later
  
  const {
    businessData,
    loadingData,
    labelsByApp,
    availableExperiences,
    showWhitelistWarning,
    deployedServices,
  } = useBusinessData(
    formState.applicationBranch,
    formState.deploymentBranch,
    formState.skipQuota,
    formState.namespace
  );

  const deployment = useDeployment();

  // Update formState with businessData when available
  React.useEffect(() => {
    if (businessData) {
      formState.initializeCheckboxStates(formState.selectedBusinessUnits);
    }
  }, [businessData]);

  const isStandalone = formState.skipQuota;

  // Drop any selectedBusinessUnits that are no longer in availableExperiences
  React.useEffect(() => {
    const filtered = formState.selectedBusinessUnits.filter(u => availableExperiences.includes(u));
    if (filtered.length !== formState.selectedBusinessUnits.length) {
      formState.setSelectedBusinessUnits(filtered);
      formState.initializeCheckboxStates(filtered);
    }
  }, [availableExperiences, formState.selectedBusinessUnits, formState.setSelectedBusinessUnits, formState.initializeCheckboxStates]);

  // Enforce on cluster/class change 
  React.useEffect(() => {
    if (isDevClusterFlag && formState.envClass === 'persistent') {
      deployment.setClassError('Cannot use persistent envs on cluster dev!');
      deployment.setDisableDeploy(true);
    } else {
      deployment.setClassError(null);
      deployment.setDisableDeploy(prev => (prev && !deployment.classError) ? prev : false);
    }
  }, [isDevClusterFlag, formState.envClass, deployment]);

  const isStep1Valid = useMemo(() => {
    const hasSelectedExperience = formState.selectedBusinessUnits.length > 0;
    return (
      formState.deploymentBranchValidated === ValidatedOptions.success &&
      formState.applicationBranchValidated === ValidatedOptions.success &&
      formState.namespaceValidated === ValidatedOptions.success &&
      hasSelectedExperience && (formState.deployRepoParams.value === '' || formState.deployRepoParams.valid !== 'error')
    );
  }, [
    formState.deploymentBranchValidated,
    formState.applicationBranchValidated,
    formState.namespaceValidated,
    formState.selectedBusinessUnits,
    formState.deployRepoParams.value,
    formState.deployRepoParams.valid
  ]);

  const isProdLike = isProdLikeCluster();
  const [budgetBlocked, setBudgetBlocked] = React.useState(false);
  const canProceedStep1 = isStep1Valid && !budgetBlocked && (isProdLike || deployment.slotsRemaining > 0);

  // Generate wizard steps inline
  const wizardSteps = useMemo((): React.ReactElement[] => {
    const steps: React.ReactElement[] = [];
    
    if (!isRefresh) {
      // Step 1: Choose Experience (and other input fields)
      steps.push(
        <WizardStep 
          key="choose-experience"
          name="Configure"
          id="menu-bu-step"
          footer={
            <Step1Footer
              isNextDisabled={!canProceedStep1}
              authError={deployment.authError}
              setAuthError={deployment.setAuthError}
            />
          }
        >
          <ChooseBusinessUnitsStep
            businessData={businessData ?? { releases: [] }}
            selectedBusinessUnits={formState.selectedBusinessUnits}
            handleBusinessUnitSelect={formState.handleBusinessUnitSelect}
            showWhitelistWarning={showWhitelistWarning}
            experiences={availableExperiences}
            loadingExperiences={loadingData}
            applicationBranch={formState.applicationBranch}
            deploymentBranch={formState.deploymentBranch}
            applicationBranchValidated={formState.applicationBranchValidated}
            deploymentBranchValidated={formState.deploymentBranchValidated}
            setDeploymentBranchValidated={formState.setDeploymentBranchValidated} 
            handleApplicationBranchChange={formState.handleApplicationBranchChange}
            handleDeploymentBranchChange={formState.handleDeploymentBranchChange}
            handleNamespaceChange={formState.handleNamespaceChange}
            namespaceValidated={formState.namespaceValidated} 
            errorMessage={formState.errorMessage}
            granularDeployment={formState.granularDeployment}
            setGranularDeployment={formState.setGranularDeployment}
            location={formState.location}
            setLocation={formState.setLocation}
            buildRailsEnv={formState.buildRailsEnv}
            setBuildRailsEnv={formState.setBuildRailsEnv}
            standalone={formState.standalone}
            setStandalone={formState.setStandalone}
            standaloneSelections={formState.standaloneSelections}
            setStandaloneSelections={formState.setStandaloneSelections}
            team={formState.team}
            setTeam={formState.setTeam}
            envClass={formState.envClass}
            setEnvClass={formState.setEnvClass}
            namespace={formState.namespace}
            classError={deployment.classError}
            setClassError={deployment.setClassError}
            setDeployDisabled={deployment.setDisableDeploy}
            setNamespace={formState.setNamespace}
            lastBuiltImage={formState.lastBuiltImage}
            setLastBuiltImage={formState.setLastBuiltImage}
            downmergeMaster={formState.downmergeMaster}
            setDownmergeMaster={formState.setDownmergeMaster}
            extraEnvVars={formState.extraEnvVars}
            setExtraEnvVars={formState.setExtraEnvVars}
            enableNewRelic={formState.enableNewRelic}
            enableRegressionProfile={formState.enableRegressionProfile}
            setEnableNewRelic={formState.setEnableNewRelic}
            setEnableRegressionProfile={formState.setEnableRegressionProfile}
            authError={deployment.authError}
            onQuotaChange={deployment.setSlotsRemaining}
            onBudgetBlockedChange={setBudgetBlocked}
            skipQuota={formState.skipQuota}
            setSkipQuota={formState.setSkipQuota}
            deployRepoParams={formState.deployRepoParams}
          />
        </WizardStep>
      );
      
      // Step 2: Dependencies step is included only if granularDeployment is true
      if (isStandalone) {
        steps.push(
          <WizardStep key="standalone-select" id="standalone-select" name="Select Apps/Infrastructure to Deploy">
            <DependencySelector
              businessData={businessData}
              selectedBusinessUnits={formState.selectedBusinessUnits}
              checkboxStates={formState.checkboxStates}
              setCheckboxStates={formState.setCheckboxStates}
              deployedServices={deployedServices}
              standaloneMode
              skipQuota={formState.skipQuota}
            />
          </WizardStep>
        );
      } else if (formState.granularDeployment) {
        steps.push(
          <WizardStep key="dependencies-to-deploy" id="dependencies-to-deploy" name="Dependencies to Deploy">
            <DependencySelector
              selectedBusinessUnits={formState.selectedBusinessUnits}
              checkboxStates={formState.checkboxStates}
              setCheckboxStates={formState.setCheckboxStates}
              businessData={businessData}
              skipQuota={formState.skipQuota}
            />
          </WizardStep>
        );
      }
    }
    
    // Step 3: Review step is always included
    steps.push(
      <WizardStep key="review" name="Review" id="review-step" footer={<Step3Footer onDeploy={deployment.handleDeploy} disableDeploy={deployment.disableDeploy} isDeploying={deployment.isDeploying}/>}>
        {deployment.isDeploying
          ? (
            <div className="dx-flex dx-flex--column dx-flex--align-center dx-flex--justify-center" style={{ height: '200px' }}>
              <div className="dx-spinner" aria-label="Triggering the deployment job…" />
              <h3 className="dx-title" style={{ marginTop: '1rem' }}>
                { deployment.title }
              </h3>
              { deployment.message }
              { deployment.queue !== 0 && <><br/>Job queue id is {deployment.queue}</>}
            </div>
          )
          : (
            <ReviewStep
              selectedBusinessUnits={formState.selectedBusinessUnits}
              checkboxStates={formState.checkboxStates}
              businessData={businessData}
              deploymentBranch={formState.deploymentBranch}
              deployRepoBranch={formState.deployRepoParams.value || 'master'}
              applicationBranch={formState.applicationBranch}
              team={formState.team}
              namespace={formState.namespace}
              location={formState.location}
              buildRailsEnv={formState.buildRailsEnv}
              standalone={formState.standalone}
              envClass={formState.envClass}
              setDisableDeploy={deployment.setDisableDeploy}
              lastBuiltImage={formState.lastBuiltImage}
              downmergeMaster={formState.downmergeMaster}
              granularDeployment={formState.granularDeployment}
              extraEnvVars={formState.extraEnvVars}
              enableNewRelic={formState.enableNewRelic}
              enableRegressionProfile={formState.enableRegressionProfile}
              authError={deployment.authError}
              initialBulkCheckItems={isRefresh && formState.refreshItems.length ? formState.refreshItems : undefined}
              skipQuota={formState.skipQuota}
              labelsByApp={labelsByApp}
              deployedServices={deployedServices}
              isRefresh={isRefresh}
            />
          )}
      </WizardStep>
    );    
    
    // Step 4: Results → show URL or error
    steps.push(
      <WizardStep key="results" name="Deploy" id="results-step" footer={<StepResultsFooter/>}>
        {deployment.isDeploying && <div className="dx-spinner" aria-label="Deploying…"/>}
        {!deployment.isDeploying && deployment.jobUrl && (
          <div style={{margin: '1rem 0'}}>
            <h3 className="dx-title">Deployment Job URL</h3>
            <a href={deployment.jobUrl} target="_blank" rel="noopener noreferrer">{deployment.jobUrl}</a>
          </div>
        )}
        {!deployment.isDeploying && deployment.deployError && (
          <Alert variant="danger" title="Deployment Error" isInline>
            {deployment.deployError}
          </Alert>
        )}
      </WizardStep>
    );
    
    return steps;
  }, [
    isRefresh,
    businessData,
    formState.selectedBusinessUnits,
    formState.handleBusinessUnitSelect,
    showWhitelistWarning,
    availableExperiences,
    loadingData,
    formState.applicationBranch,
    formState.deploymentBranch,
    formState.applicationBranchValidated,
    formState.deploymentBranchValidated,
    formState.setDeploymentBranchValidated,
    formState.handleApplicationBranchChange,
    formState.handleDeploymentBranchChange,
    formState.handleNamespaceChange,
    formState.namespaceValidated,
    formState.errorMessage,
    formState.granularDeployment,
    formState.setGranularDeployment,
    formState.location,
    formState.setLocation,
    formState.buildRailsEnv,
    formState.setBuildRailsEnv,
    formState.standalone,
    formState.setStandalone,
    formState.standaloneSelections,
    formState.setStandaloneSelections,
    formState.team,
    formState.setTeam,
    formState.envClass,
    formState.setEnvClass,
    formState.namespace,
    deployment.classError,
    deployment.setClassError,
    deployment.setDisableDeploy,
    formState.setNamespace,
    formState.lastBuiltImage,
    formState.setLastBuiltImage,
    formState.downmergeMaster,
    formState.setDownmergeMaster,
    formState.extraEnvVars,
    formState.setExtraEnvVars,
    formState.enableNewRelic,
    formState.enableRegressionProfile,
    formState.setEnableNewRelic,
    formState.setEnableRegressionProfile,
    deployment.authError,
    deployment.setAuthError,
    deployment.setSlotsRemaining,
    formState.skipQuota,
    formState.setSkipQuota,
    formState.deployRepoParams,
    formState.checkboxStates,
    formState.setCheckboxStates,
    deployedServices,
    labelsByApp,
    formState.refreshItems,
    deployment.isDeploying,
    deployment.jobUrl,
    deployment.deployError,
    deployment.handleDeploy,
    deployment.disableDeploy,
    canProceedStep1,
    isStandalone,
  ]);

   // Render content based on route match
  const renderContent = (): React.ReactNode => {
    if (match) {
      return (
        <div style={{ position: 'relative', margin: '-28px -32px', height: 'calc(100% + 56px)' }}>
          {deployment.clusterAuthError && (
            <Alert
              variant="danger"
              isInline
              title={deployment.clusterAuthError}
              style={{ width: '100%', marginBottom: '1rem', padding: '0 24px' }}
            />
          )}
          <Wizard
            style={{ height: '100%' }}
            key={formState.granularDeployment ? 'with-dependencies' : 'no-dependencies'}
            header={
                <div className="dx-flex dx-flex--justify-between" style={{ width: '100%', padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-primary)' }}>Manage Deployment</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '6px' }}>Configure and deploy your environment</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Target Cluster</span>
                    <ClusterPicker onAuthError={deployment.setClusterAuthError} />
                  </div>
                </div>
              }
            onClose={() => history.push('/')}
            onSave={async () => {
              await deployment.handleDeploy();
            }}
            isVisitRequired={true}
          >
            {wizardSteps}
          </Wizard>
        </div>
      );
    } else {
      return (
        <>
          {deployment.isEligible ? (
            <Button variant="primary" onClick={() => history.push(WIZARD_ROUTE)}>
              Manage Environment
            </Button>
          ) : (
            <Popover
              position="top"
              headerContent="Access Denied"
              bodyContent="You do not have the required permissions to create a new environment."
              alertSeverityVariant="warning"
              headerIcon={<ExclamationTriangleIcon />}
            >
              <Button variant="primary" isAriaDisabled>
                Manage Environment
              </Button>
            </Popover>
          )}
        </>
      );
    }
  };

  return (
    <>
      {renderContent()}
    </>
  );
};

export { CreateEnvWizard }
